const AMSTERDAM_TIME_ZONE = "Europe/Amsterdam";

export function toTimestampMs(value) {
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp)) return null;
  return timestamp < 1e12 ? timestamp * 1000 : timestamp;
}

export function getAmsterdamDateKey(value = Date.now()) {
  const timestamp = value instanceof Date ? value.getTime() : toTimestampMs(value);
  if (timestamp == null) return "";

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("en-CA", {
    timeZone: AMSTERDAM_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

export function isTodayOrFutureAmsterdam(value) {
  const dateKey = getAmsterdamDateKey(value);
  if (!dateKey) return false;

  return dateKey >= getAmsterdamDateKey(Date.now());
}

// De kalenderdag van een datum-epoch, als Date waarvan de *lokale* dag-maand-jaar
// die dag zijn. Elke `date` die de API teruggeeft is een epoch op middernacht
// Europe/Amsterdam (2026-09-22 00:00 CEST = 2026-09-21 22:00 UTC), dus wie hem
// met de getters van de browser zelf uitleest, krijgt west van CEST de dag
// ervoor: een browser op UTC, of een die in mail-isolatie draait (ClickUp
// 1240cceatp1 — Vesting Finance zag "21 sept." via Mimecast Browser Isolation).
// De weergave eromheen blijft zoals hij was: de Date die hier uit komt schrijft
// in de tijdzone van de lezer dezelfde dag als hier in Amsterdam. Binnen
// Amsterdam verandert er dus niets.
//
// Alleen voor het tonen van een dag. Rekenen met het moment zelf (een
// annuleertermijn, een sorteersleutel) doe je op de epoch, niet hierop.
export function toAmsterdamCalendarDate(value) {
  const timestamp = value instanceof Date ? value.getTime() : toTimestampMs(value);
  // Een ongeldige waarde gaf al een Invalid Date; dat blijft zo, zodat een
  // toLocaleDateString() erop nog steeds niet klapt.
  if (timestamp == null) return new Date(NaN);

  const dateKey = getAmsterdamDateKey(timestamp);
  if (!dateKey) return new Date(NaN);

  const [year, month, day] = dateKey.split("-").map(Number);
  return new Date(year, month - 1, day);
}

// Het omgekeerde van toAmsterdamCalendarDate: de lokale kalenderdag van een Date
// (of van de JSON-string die een kopie in de wizard ervan maakt) als het moment
// van middernacht Europe/Amsterdam op die dag.
//
// Voor wat terug naar de API gaat. Stuur je een lokale middernacht zelf, dan
// leest de API (creditplafond, bevestigingsmail, agenda-afspraak) oost van
// Amsterdam de dag ervoor. Middernacht Amsterdam is precies wat een browser in
// Amsterdam altijd al stuurde, dus de API merkt geen verschil.
export function toAmsterdamMidnight(value) {
  const local = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(local.getTime())) return new Date(NaN);

  // Om 00:00 UTC is het in Amsterdam 01:00 of 02:00 op dezelfde dag, en tussen
  // die twee middernachten wisselt de zomertijd nooit (dat gebeurt om 01:00 UTC).
  const utcMidnight = Date.UTC(local.getFullYear(), local.getMonth(), local.getDate());
  const hour = Number(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: AMSTERDAM_TIME_ZONE,
      hour: "numeric",
      hourCycle: "h23",
    }).format(utcMidnight)
  );
  return new Date(utcMidnight - hour * 3600000);
}

// Een boeking uit de aanmeldwizard zoals de API hem leest: elke gekozen
// `dates[].date` als middernacht Amsterdam. Een epoch uit de API is dat al en
// blijft staan.
export function toBookingPayload(item) {
  if (!Array.isArray(item.dates)) return item;

  return {
    ...item,
    dates: item.dates.map((entry) =>
      entry && (entry.date instanceof Date || typeof entry.date === "string")
        ? { ...entry, date: toAmsterdamMidnight(entry.date) }
        : entry
    ),
  };
}
